import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {EstadoChecklistMiaGestor} from './mia-gestor.service';
import {
  BotaoGestor,
  ConversaGestorStore,
  InterativoGestor,
  TrechoMensagemGestor,
  trechosDaMensagem,
} from './conversa-gestor.store';

interface PassoImplantacao {
  chave: string;
  rotulo: string;
  estado: 'pendente' | 'andamento' | 'concluido' | 'atencao';
  detalhe: string;
}

/**
 * Copiloto de implantação — a conversa é a tela inteira.
 *
 * Dentro do chat só entra o que o WhatsApp sabe renderizar: bolha de texto,
 * botões de resposta, lista e cta_url. Nada de cartão-painel no meio das
 * mensagens — o que a Mia propõe é uma bolha com botão, e o que é grande demais
 * para a bolha abre por cima (a gaveta da revisão, a folha da lista), que é o
 * mesmo gesto do canal.
 *
 * O que não é mensagem em canal nenhum — id de empresa, zona de DNS, contadores
 * — mora na ficha, atrás do cabeçalho da janela. O WhatsApp tem essa tela: é
 * tocar no nome do contato.
 */
@Component({
  selector: 'app-painel-mia-gestor',
  templateUrl: './painel-mia-gestor.component.html',
  styleUrls: ['./painel-mia-gestor.component.scss'],
  providers: [ConversaGestorStore],
})
export class PainelMiaGestorComponent implements OnInit {
  @ViewChild('campoMensagem') campoMensagem: ElementRef;
  @ViewChild('listaMensagens') listaMensagens: ElementRef;

  mensagem = '';
  gavetaAberta = false;
  /** string em vez de união literal: o template atribui direto no (click) */
  filtroRascunho = 'todos';
  produtoAberto: string | null = null;
  arrastando = false;
  confirmandoReinicio = false;
  fichaAberta = false;
  /** lista aberta a partir de uma bolha — o equivalente à folha do list message */
  folha: InterativoGestor | null = null;

  grupos: Array<{categoria: any; produtos: any[]; travada: boolean}> = [];
  contagens: {[filtro: string]: number} = {todos: 0, pendencia: 0, 'sem-preco': 0, importados: 0};
  private importados = new Set<string>();

  constructor(public conversa: ConversaGestorStore) {}

  ngOnInit(): void {
    this.conversa.inicie().then(() => this.roleAteOFim());
  }

  // ----- conversa -------------------------------------------------------

  aoPressionarTecla(evento: KeyboardEvent): void {
    if (evento.key !== 'Enter' || evento.shiftKey || (evento as any).isComposing) return;
    evento.preventDefault();
    this.envie();
  }

  envie(): void {
    const texto = this.mensagem;
    if (!texto.trim() || this.conversa.enviando) return;
    this.mensagem = '';
    this.roleAteOFim();
    this.conversa.envie(texto).then(() => {
      this.roleAteOFim();
      setTimeout(() => this.campoMensagem && this.campoMensagem.nativeElement.focus(), 0);
    });
  }

  partesDaMensagem(texto: string): TrechoMensagemGestor[] {
    return trechosDaMensagem(texto);
  }

  /**
   * Toque no botão da bolha. 'texto' é o reply button puro — manda o rótulo como
   * se tivesse sido digitado, igual ao canal. As outras ações são o que o painel
   * faz no lugar da página que o cta_url abriria no celular.
   */
  aoTocarBotao(botao: BotaoGestor): void {
    if (!this.botaoAtivo(botao)) return;
    if (botao.acao === 'texto') {
      this.mensagem = botao.rotulo;
      this.envie();
      return;
    }
    if (botao.acao === 'aprovar') return this.aprove();
    if (botao.acao === 'cancelar') return this.cancele();
    if (botao.acao === 'importar') return this.prepareImportacao();
    if (botao.acao === 'revisar') return this.abraGaveta();
    if (botao.acao === 'abrir' && botao.href) window.open(botao.href, '_blank', 'noopener');
  }

  /**
   * Botão gasto continua visível, como no WhatsApp: quem tocou vê o que tocou.
   * O que muda é que ele para de responder.
   */
  botaoAtivo(botao: BotaoGestor): boolean {
    if (this.conversa.enviando || this.conversa.carregando) return false;
    if (botao.acao === 'aprovar' || botao.acao === 'cancelar') {
      return !!this.conversa.acaoPendente && !this.conversa.operandoAcao;
    }
    if (botao.acao === 'importar') {
      return this.conversa.podeRevisarCardapio && !this.conversa.operandoCardapio &&
        !!this.conversa.referenciasSelecionadas.length;
    }
    if (botao.acao === 'revisar') {
      return this.conversa.podeRevisarCardapio && !this.conversa.operandoCardapio;
    }
    return true;
  }

  // ----- faixa de progresso (só leitura) --------------------------------

  /** o getter monta objetos novos a cada ciclo; sem trackBy o ngFor recriava os <li> e a animação de entrada repetia */
  chaveDoPasso(_indice: number, passo: PassoImplantacao): string {
    return passo.chave;
  }

  get passos(): PassoImplantacao[] {
    const lista = this.conversa.checklist;
    return [
      this.monteP('empresa', 'Empresa', lista.empresa, this.conversa.empresaId
        ? `#${this.conversa.empresaId}` : ''),
      this.monteP('cardapio', 'Cardápio', lista.cardapio, this.detalheDoCardapio()),
      this.monteP('entrega', 'Entrega', lista.formasEntrega, ''),
      this.monteP('link', 'Link', lista.link, ''),
    ];
  }

  private monteP(
    chave: string,
    rotulo: string,
    estadoChecklist: EstadoChecklistMiaGestor,
    complemento: string
  ): PassoImplantacao {
    let estado: PassoImplantacao['estado'] = 'pendente';
    if (estadoChecklist === 'CONCLUIDO' || estadoChecklist === 'DISPONIVEL') estado = 'concluido';
    else if (estadoChecklist === 'ERRO' || estadoChecklist === 'PARCIAL') estado = 'atencao';
    else if (estadoChecklist !== 'PENDENTE') estado = 'andamento';

    const detalhes: {[estado: string]: string} = {
      PENDENTE: 'aguardando',
      EM_ANDAMENTO: 'em andamento',
      PARCIAL: 'parcial',
      CONCLUIDO: 'concluído',
      DNS_PENDENTE: 'publicação pendente',
      DISPONIVEL: 'disponível',
      ERRO: 'requer atenção',
    };
    const base = detalhes[estadoChecklist] || String(estadoChecklist).toLowerCase();
    return {chave, rotulo, estado, detalhe: complemento ? `${base} · ${complemento}` : base};
  }

  private detalheDoCardapio(): string {
    const analise = this.conversa.analiseCardapio;
    if (!analise || !analise.estatisticas) return '';
    return `${analise.estatisticas.produtos} itens`;
  }

  // ----- anexos ---------------------------------------------------------

  selecioneArquivos(evento: Event): void {
    const entrada = evento.target as HTMLInputElement;
    const arquivos = entrada.files ? Array.from(entrada.files) : [];
    entrada.value = '';
    this.envieArquivos(arquivos);
  }

  aoArrastar(evento: DragEvent, entrando: boolean): void {
    if (!this.conversa.podeEnviarCardapio) return;
    evento.preventDefault();
    this.arrastando = entrando;
  }

  aoSoltar(evento: DragEvent): void {
    if (!this.conversa.podeEnviarCardapio) return;
    evento.preventDefault();
    this.arrastando = false;
    const arquivos = evento.dataTransfer ? Array.from(evento.dataTransfer.files) : [];
    this.envieArquivos(arquivos);
  }

  private envieArquivos(arquivos: File[]): void {
    if (!arquivos.length) return;
    this.roleAteOFim();
    this.conversa.anexeEAnalise(arquivos).then(() => {
      this.recalculeGrupos();
      this.roleAteOFim();
    });
  }

  // ----- folha da lista e ficha da implantação --------------------------

  /** list message: no celular abre folha nativa; aqui abre a mesma folha */
  abraFolha(interativo: InterativoGestor): void {
    this.folha = interativo;
  }

  fecheFolha(): void {
    this.folha = null;
  }

  abraFicha(): void {
    this.fichaAberta = true;
  }

  fecheFicha(): void {
    this.fichaAberta = false;
  }

  // ----- gaveta do rascunho ---------------------------------------------

  abraGaveta(): void {
    this.gavetaAberta = true;
    this.produtoAberto = null;
    this.recalculeGrupos();
  }

  fecheGaveta(): void {
    this.gavetaAberta = false;
  }

  alterneProduto(referencia: string): void {
    this.produtoAberto = this.produtoAberto === referencia ? null : referencia;
  }

  salveRascunho(): void {
    // o servidor devolve o rascunho versionado: os objetos ligados aos campos
    // trocam, então a lista precisa ser remontada
    this.conversa.salveRascunho().then(() => {
      this.recalculeGrupos();
      this.roleAteOFim();
    });
  }

  prepareImportacao(): void {
    this.conversa.prepareImportacao().then(() => {
      if (this.conversa.acaoPendente) this.gavetaAberta = false;
      this.roleAteOFim();
    });
  }

  /**
   * A lista da gaveta é um campo, não um getter: getter recriaria o array a
   * cada ciclo de detecção (o campo em edição perderia o foco) e o grupo
   * aponta para a categoria ORIGINAL — copiar faria a edição do nome cair
   * numa cópia e sumir ao salvar.
   */
  mudeFiltro(filtro: string): void {
    this.filtroRascunho = filtro;
    this.recalculeGrupos();
  }

  recalculeGrupos(): void {
    const analise = this.conversa.analiseCardapio;
    const rascunho = analise && analise.rascunho;

    this.importados = new Set<string>();
    (analise && analise.itens || []).forEach(item => {
      if (item.status === 'IMPORTADO') this.importados.add(item.referencia);
    });

    if (!rascunho) {
      this.grupos = [];
      this.contagens = {todos: 0, pendencia: 0, 'sem-preco': 0, importados: 0};
      return;
    }

    this.contagens = {todos: 0, pendencia: 0, 'sem-preco': 0, importados: 0};
    rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => {
        this.contagens.todos++;
        if (this.passaNoFiltro(produto, 'pendencia')) this.contagens.pendencia++;
        if (this.passaNoFiltro(produto, 'sem-preco')) this.contagens['sem-preco']++;
        if (this.passaNoFiltro(produto, 'importados')) this.contagens.importados++;
      }));

    this.grupos = rascunho.categorias
      .map((categoria: any) => ({
        categoria,
        produtos: categoria.produtos.filter((produto: any) => this.passaNoFiltro(produto)),
        // renomear a categoria depois que um produto dela entrou quebraria o
        // vínculo do que já foi gravado
        travada: categoria.produtos.some((produto: any) => this.importados.has(produto.referencia)),
      }))
      .filter((grupo: any) => grupo.produtos.length);
  }

  jaImportado(referencia: string): boolean {
    return this.importados.has(referencia);
  }

  private passaNoFiltro(produto: any, filtro: string = this.filtroRascunho): boolean {
    if (filtro === 'sem-preco') return this.conversa.produtoTemBloqueio(produto);
    if (filtro === 'importados') return this.conversa.produtoJaImportado(produto.referencia);
    if (filtro === 'pendencia') {
      return this.conversa.produtoTemBloqueio(produto) || !!(produto.pendencias || []).length;
    }
    return true;
  }

  motivoDoBloqueio(produto: any): string {
    const motivo = this.conversa.motivoDoBloqueio(produto);
    if (motivo) return motivo;
    const pendencias = produto.pendencias || [];
    return pendencias.length ? `${String(pendencias[0].mensagem || pendencias[0])} — confira o item.` : '';
  }

  ehPizza(produto: any): boolean {
    return produto.tipoProduto === 'PIZZA';
  }

  /** pizza não tem preço único: o campo vira "a partir de" e o valor mora em cada tamanho */
  precoDaPizza(produto: any): string {
    const grupo = this.conversa.grupoDeTamanhos(produto);
    const precos = ((grupo && grupo.opcoes) || [])
      .map((opcao: any) => Number(opcao.preco))
      .filter((preco: number) => Number.isFinite(preco) && preco >= 0);
    if (!precos.length) return 'preço por tamanho';
    return `a partir de R$ ${Math.min(...precos).toFixed(2).replace('.', ',')}`;
  }

  // ----- ações ----------------------------------------------------------

  aprove(): void {
    this.conversa.aprove().then(() => this.roleAteOFim());
  }

  cancele(): void {
    this.conversa.cancele().then(() => this.roleAteOFim());
  }

  tentarDeNovo(): void {
    this.conversa.verifiqueAgente();
  }

  reinicie(): void {
    this.confirmandoReinicio = false;
    this.gavetaAberta = false;
    this.fichaAberta = false;
    this.folha = null;
    this.produtoAberto = null;
    this.conversa.reinicie().then(() => {
      this.recalculeGrupos();
      this.roleAteOFim();
    });
  }

  private roleAteOFim(): void {
    setTimeout(() => {
      if (!this.listaMensagens) return;
      const lista = this.listaMensagens.nativeElement;
      lista.scrollTop = lista.scrollHeight;
    }, 0);
  }
}
